import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InventoryServiceConfiguration } from '../../../../data/models/inventory-service-configuration.model';
import { useRef, useState } from 'react';
import omit from 'lodash/omit';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import * as tripLegSlice from '../../../../data/tripleg.slice';
import { DaysBeforeDueForm } from './DaysBeforeDueForm';
import { TimeToAcceptForm } from './TImeToAcceptForm';
import { TripLegValueDistanceForm } from './TripLegValueDistanceForm';
import { SizeOfPackageForm } from './SizeOfPackageForm';
import { PackageOfferTimeForm } from './PackageOfferTimeForm';
import { DateTime } from 'luxon';
import { WeightedRandomizationForm } from './WeightedRandomizationForm';

export interface InventoryManagementSettingFormRef {
  getValues: () => any;
  triggerValidation: () => Promise<boolean>;
}

export interface InventoryManagementFormProps {
  onValidityChange?: (valid: boolean) => void;
}

interface InventoryManagementDialogProps {
  open: boolean;
  title: string;
  description: string;
  propertyId: keyof InventoryServiceConfiguration | null;
  serviceConfiguration?: InventoryServiceConfiguration;
  handleClose: () => void;
  handleViewActivity: () => void;
  isReadOnly?: boolean;
}

const PREFIX = 'InventoryManagementDialog';

const classes = {
  subtitle: `${PREFIX}-subtitle`,
  description: `${PREFIX}-description`,
  closeButtonContainer: `${PREFIX}-closeButtonContainer`,
  actionButtonsContainer: `${PREFIX}-actionButtonsContainer`,
  actionButton: `${PREFIX}-actionButton`,
  viewActivityButton: `${PREFIX}-viewActivityButton`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogTitle-root ': {
    ...theme.typography.h3,
  },
  '& .MuiDialog-paper': {
    width: 444,
  },
  '& .MuiDialogActions-root': {
    justifyContent: 'space-between',
  },
  [`& .${classes.subtitle}`]: {
    marginTop: 4,
  },
  [`& .${classes.description}`]: {
    marginBottom: theme.spacing(3),
  },
  [`& .${classes.closeButtonContainer}`]: {
    display: 'flex',
    justifyContent: 'end',
  },
  [`& .${classes.actionButtonsContainer}`]: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  [`& .${classes.actionButton}`]: {
    width: 111.5,
    height: 48,
  },
  [`& .${classes.viewActivityButton}`]: {
    paddingLeft: 0,
    paddingRight: 0,
    marginLeft: -25,
  },
}));

export function InventoryManagementDialog({
  open,
  title,
  description,
  propertyId,
  serviceConfiguration,
  handleClose,
  handleViewActivity,
  isReadOnly = false,
}: InventoryManagementDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [isFormValid, setIsFormValid] = useState(false);
  const formRef = useRef<InventoryManagementSettingFormRef>(null);

  const getFormComponent = () => {
    switch (propertyId) {
      case 'tripLegNearDueThresholdDays':
        return (
          <DaysBeforeDueForm
            ref={formRef}
            value={serviceConfiguration?.[propertyId]}
            onValidityChange={setIsFormValid}
            isReadOnly={isReadOnly}
          />
        );
      case 'packageOfferingTimeoutMinutes':
        return (
          <TimeToAcceptForm
            ref={formRef}
            value={serviceConfiguration?.[propertyId]}
            onValidityChange={setIsFormValid}
            isReadOnly={isReadOnly}
          />
        );
      case 'tripLegValuationDistanceThreshold':
        return (
          <TripLegValueDistanceForm
            ref={formRef}
            value={serviceConfiguration?.[propertyId]}
            onValidityChange={setIsFormValid}
            isReadOnly={isReadOnly}
          />
        );
      case 'targetPackageSize':
        return (
          <SizeOfPackageForm
            ref={formRef}
            value={serviceConfiguration?.targetPackageSize.percentCapacitySize}
            onValidityChange={setIsFormValid}
            isReadOnly={isReadOnly}
          />
        );
      case 'packageOfferingTimes':
        return (
          <PackageOfferTimeForm
            ref={formRef}
            value={serviceConfiguration?.packageOfferingTimes.times}
            onValidityChange={setIsFormValid}
            isReadOnly={isReadOnly}
          />
        );
      case 'weightedRandomization':
        return (
          <WeightedRandomizationForm
            ref={formRef}
            value={serviceConfiguration?.weightedRandomization}
            onValidityChange={setIsFormValid}
            isReadOnly={isReadOnly}
          />
        );
    }
  };

  const handleSave = async () => {
    if (!propertyId || !serviceConfiguration) {
      return;
    }
    const valid = await formRef.current?.triggerValidation();
    if (!valid) {
      return;
    }

    const values = formRef.current?.getValues();
    let value = values[propertyId];

    if (!value) {
      return;
    }

    if (
      propertyId === 'tripLegNearDueThresholdDays' ||
      propertyId === 'packageOfferingTimeoutMinutes' ||
      propertyId === 'tripLegValuationDistanceThreshold'
    ) {
      value = Number(value);
    } else if (propertyId === ('packageOfferTime' as any)) {
      value = value.map((dateTime: { value: DateTime }) =>
        dateTime.value.toFormat('HH:mm:ss'),
      );
    } else if (propertyId === 'weightedRandomization') {
      value = {
        priorityFactorCoefficient: Number(value.priorityFactorCoefficient),
        scoreFactorCoefficient: Number(value.scoreFactorCoefficient),
        capacityFactorCoefficient: Number(value.capacityFactorCoefficient),
      };
    } else if (propertyId === 'targetPackageSize') {
      value = {
        percentCapacitySize: Number(value) / 100,
        defaultSize: serviceConfiguration.targetPackageSize.defaultSize,
      };
    } else if (propertyId === 'packageOfferingTimes') {
      const formattedTimes = value.map((val: { value: DateTime }) =>
        val.value.toFormat('HH:mm:ss'),
      );
      value = {
        timeZone: serviceConfiguration.packageOfferingTimes.timeZone,
        times: formattedTimes,
      };
    }

    const updatedServiceConfig = {
      ...omit(serviceConfiguration, [
        'correlationUUID',
        'modifiedByUserID',
        'modifiedDateTimeUTC',
      ]),
    } as InventoryServiceConfiguration;

    (updatedServiceConfig as any)[propertyId] = value;
    dispatch(tripLegSlice.saveServiceConfigurationAsync(updatedServiceConfig));
    handleClose();
  };

  return (
    <StyledDialog open={open}>
      <DialogTitle>
        {title}
        <Typography variant="body1" className={classes.subtitle}>
          {t('inventoryManagement.global')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography className={classes.description}>{description}</Typography>
        {getFormComponent()}
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.viewActivityButton}
          onClick={handleViewActivity}>
          {t('inventoryManagement.viewActivity')}
        </Button>
        <Box className={classes.actionButtonsContainer}>
          {isReadOnly && (
            <>
              <Box className={classes.actionButton} />
              <Button
                variant="contained"
                color="primary"
                size="large"
                className={classes.actionButton}
                onClick={handleClose}>
                {t('common.close')}
              </Button>
            </>
          )}
          {!isReadOnly && (
            <>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                className={classes.actionButton}
                onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                className={classes.actionButton}
                disabled={!isFormValid}
                onClick={handleSave}
                autoFocus>
                {t('common.save')}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </StyledDialog>
  );
}
